import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useConversationStoreShallow } from 'src/store/conversationStore';

import { ConversationLayout } from '@components/templates/ConversationLayout/ConversationLayout';

import { useSubmitCaseReport } from '@utils/hooks/useReportCaseSubmit';
import { useResponsive } from '@utils/hooks/useResponsive';

import { getPath } from '@services/RouteService/paths';

import { useReactiveVar } from '@apollo/client';
import { authVar, conversationSubmitLoadingVar } from '@boot/reactiveVariables';
import { ReportSummary } from '@domains/new-conversation/organisms/ReportSummary/ReportSummary';
import { ReportUpload } from '@domains/new-conversation/organisms/ReportUpload/ReportUpload';
import {
    ContentScrollbar,
    ProceedButton,
} from '@domains/new-conversation/pages/Conversation.styled';
import { ReviewContainer } from '@domains/new-conversation/pages/ReportReviewPage/ReportReviewPage.styled';
import { useConversationRedirection } from '@domains/new-conversation/useConversationRedirection';

export const ReportReviewPage = () => {
    useConversationRedirection();

    const { loading: conversationLoading } = useConversationStoreShallow([
        'loading',
    ]);

    const { caseUuid } = useParams<{ caseUuid: string }>();
    const isGuest = useReactiveVar(authVar).isGuest;

    const isMd = useResponsive('md');
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [submitCaseReport, { loading }] = useSubmitCaseReport();

    const handleSubmit = () => {
        if (isGuest) {
            navigate(
                getPath({
                    params: { caseUuid },
                    path: '/new-conversation/:caseUuid/additional-information',
                }),
            );

            return;
        }

        conversationSubmitLoadingVar(true);

        void submitCaseReport({
            variables: {
                input: { uuid: caseUuid ?? '' },
            },
        });
    };

    return (
        <ConversationLayout
            sidebarProps={{
                buttons: (
                    <ProceedButton
                        disabled={conversationLoading}
                        isLoading={loading}
                        onClick={handleSubmit}
                        testId="button_continue-submit"
                        variant={'primary'}
                    >
                        {t(isGuest ? 'CONTINUE' : 'SUBMIT_REPORT')}
                    </ProceedButton>
                ),
                currentStep: 3,
                preventionModalActive: true,
            }}
        >
            <ReviewContainer>
                {isMd ? (
                    <>
                        <ReportUpload />
                        <ReportSummary />
                    </>
                ) : (
                    <ContentScrollbar>
                        <ReportUpload />
                        <ReportSummary />
                    </ContentScrollbar>
                )}
            </ReviewContainer>
        </ConversationLayout>
    );
};
